package diana.soleil.design.iraniancanadienbazzar.feature.business

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ImagePickController
import diana.soleil.design.iraniancanadienbazzar.core.data.UserProfileRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.ClaimRole
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/** Where the claim submission stands (drives the button/banners/success panel on the screen). */
enum class ClaimSubmitStatus {
    /** Real (non-anonymous) account required — the route sends the user to sign-in. */
    SignInRequired,
    Failed,
    Submitted,
}

/** UI state for the Claim Business form. The editable fields themselves are local screen state. */
data class ClaimBusinessUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    /** Canonical business name (denormalized onto the claim + shown in the header). */
    val businessName: String = "",
    /** Prefilled claimant name from the signed-in profile (blank for guests/no doc). */
    val prefillName: String = "",
    /** A proof photo is attached to the draft (uploaded with the claim on submit). */
    val proofAttached: Boolean = false,
    val submitting: Boolean = false,
    val submitStatus: ClaimSubmitStatus? = null,
)

/** The verification details the claimant fills in on the form and hands back on submit. */
data class ClaimSubmission(
    val claimantName: String,
    val role: ClaimRole,
    val contactEmail: String,
    val contactPhone: String,
    val message: String,
)

/**
 * Loads the target business (for the header + denormalized name), prefills the claimant name from the
 * signed-in profile, and submits a verification-backed claim to [BusinessRepository.submitClaim]
 * (`claims/{id}` + a best-effort `claim_requested` flip). Guests get [ClaimSubmitStatus.SignInRequired]
 * so the route can route them to sign-in — auth itself is not handled here.
 */
class ClaimBusinessViewModel(
    private val businessRepository: BusinessRepository,
    userProfileRepository: UserProfileRepository,
    private val imagePicker: ImagePickController,
    private val businessId: String,
) : ViewModel() {

    private val loadState = MutableStateFlow(ClaimBusinessUiState())
    private val attachedProof = MutableStateFlow<PickedImage?>(null)

    val state: StateFlow<ClaimBusinessUiState> =
        combine(loadState, userProfileRepository.observeProfile(), attachedProof) { s, profile, proof ->
            val name = (profile as? DataResult.Success)?.data?.displayName?.takeIf { it.isNotBlank() }
            s.copy(prefillName = name ?: s.prefillName, proofAttached = proof != null)
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), ClaimBusinessUiState())

    init { load() }

    fun retry() {
        loadState.value = ClaimBusinessUiState(loading = true)
        load()
    }

    fun submit(submission: ClaimSubmission) {
        if (loadState.value.submitting) return
        viewModelScope.launch {
            loadState.value = loadState.value.copy(submitting = true, submitStatus = null)
            val result = businessRepository.submitClaim(
                businessId = businessId,
                businessName = loadState.value.businessName,
                claimantName = submission.claimantName,
                role = submission.role,
                contactEmail = submission.contactEmail,
                contactPhone = submission.contactPhone,
                message = submission.message,
                proof = attachedProof.value,
            )
            loadState.value = loadState.value.copy(
                submitting = false,
                submitStatus = when (result) {
                    is DataResult.Success -> ClaimSubmitStatus.Submitted
                    is DataResult.Failure ->
                        if (result.error is AppError.Unauthorized) ClaimSubmitStatus.SignInRequired
                        else ClaimSubmitStatus.Failed
                },
            )
        }
    }

    /** Opens the system photo picker and attaches the result as the ownership proof. */
    fun attachProof() {
        viewModelScope.launch {
            imagePicker.pick()?.let { attachedProof.value = it }
        }
    }

    fun removeProof() {
        attachedProof.value = null
    }

    /** Clears a shown submit status after the UI (route/banner) has consumed it. */
    fun consumeSubmitStatus() {
        loadState.value = loadState.value.copy(submitStatus = null)
    }

    private fun load() {
        viewModelScope.launch {
            loadState.value = when (val result = businessRepository.getBusiness(businessId)) {
                is DataResult.Success ->
                    loadState.value.copy(loading = false, error = false, businessName = result.data.name)
                is DataResult.Failure -> loadState.value.copy(loading = false, error = true)
            }
        }
    }
}
